import Vue from 'vue'
import VueRouter from 'vue-router'
import VueMeta from 'vue-meta'
import NProgress from 'nprogress/nprogress'
import store from '@state/store'
import P from '@services/parameters'
import * as Sentry from '@sentry/browser'
import { isChunkLoadError, handleChunkError } from '@utils/chunkErrorHandler'
import { captureLoginMethodFromQuery } from '@utils/loginTracking'
import { resolveClienteHiddenRedirect } from '@modules/navegacao-v2/constants/navigation-modules'
import { applyClienteDaQueryGuard } from '@modules/navegacao-v2/utils/cliente-da-query'
import { routeModuleKey } from '@modules/navegacao-v2/utils/visible-modules'
import { GlobalBus } from '@services/bus'
import { CENTRAL_CONFIG_FOCUS_GUIDE } from '@modules/central-config/constants/Text'
import { isCliente } from '@modules/financial-planning/utils/client-permissions'
import { deveVerSoFeeFixo } from '@utils/escritorioFeeFixoMigracao'
import { estaNaListaDeEspera } from '@modules/professional-waitlist/utils/lista-espera-enabled'
import { FEATURES } from '@constants/Text'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'
import { resolveFeeFixoRedirect } from '@modules/fee-fixo/routes'
import { checkImersivaPermission } from '@modules/aawz-intelligence/routes'
import { ROTA_IMERSIVA } from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'
import routes from './routes'

Vue.use(VueRouter)
Vue.use(VueMeta, {
  // The component option name that vue-meta looks for meta info on.
  keyName: 'page',
})

// Fixes AAWZ-WEB-3NK, AAWZ-WEB-3M8, AAWZ-WEB-33V, AAWZ-WEB-3HZ, AAWZ-WEB-3VT
// Swallow NavigationCancelled/Redirected/Duplicated/Aborted and rethrow real errors.
// ChunkLoadErrors are also swallowed here because router.onError already handles
// them via handleChunkError() — letting the rejection propagate would only create
// noise in window.onunhandledrejection / Sentry.
export function swallowNavigationFailure(err) {
  if (VueRouter.isNavigationFailure(err)) return undefined
  if (isChunkLoadError(err)) return undefined
  throw err
}

// Abas escondidas em contexto cliente (CLIENTE_HIDDEN_ROUTES) não tinham
// guard — acesso direto por URL carregava a rota normalmente, mas nenhuma
// aba ficava marcada como ativa no NavBarNavigation (foi filtrada do array).
// Extraída pra função própria (mesmo padrão de swallowNavigationFailure
// acima) só pra ficar testável sem precisar montar o beforeEach inteiro.
// Retorna true quando redirecionou (guard.beforeEach deve parar ali).
export function applyClienteHiddenRouteGuard(routeTo, next) {
  if (!store.getters['navigation/isClienteContext']) return false
  const clienteRedirect = resolveClienteHiddenRedirect(routeTo.name)
  if (!clienteRedirect || clienteRedirect === routeTo.name) return false
  next({ name: clienteRedirect })
  return true
}

// O mesmo veredito do bloco de `customPermission` do beforeEach (ele roda
// DEPOIS de `applyClienteGuards`), com os mesmos argumentos. Rota sem a meta
// passa.
export function rotaPassaNoCustomPermission(routeTo) {
  const customPermission = routeTo && routeTo.meta && routeTo.meta.customPermission
  if (!customPermission) return true
  return Boolean(
    customPermission({
      user: store.getters['user/currentUser'] || {},
      show: store.getters['parameters/show'],
    })
  )
}

// Os dois guards de cliente, NESTA ordem: `?cliente=<id>` entra no cliente
// primeiro (é assim que uma aba nova abre um módulo agregado já na visão do
// cliente — `navegacao-v2/utils/cliente-da-query.js`), e só então a rota
// escondida em contexto cliente é redirecionada para a entrada certa.
// ⚠️ Entrar no cliente é side-effect (contexto da aba, `sessionStorage`), e
// o `customPermission` só é conferido mais abaixo no beforeEach: sem a
// checagem aqui, uma URL com `?cliente=` para rota que termina em 404 deixava
// a aba em contexto "Cliente: X" (review do PR #2261, I-20). A ordem dos
// guards não muda — só o `?cliente=` deixa de entrar quando a rota vai cair.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export async function applyClienteGuards(routeTo, next) {
  if (rotaPassaNoCustomPermission(routeTo)) {
    await applyClienteDaQueryGuard(routeTo, store)
  }
  return applyClienteHiddenRouteGuard(routeTo, next)
}

// Caminho da raiz do módulo. É a ORIGEM do redirect que identifica o caso, não
// o destino — ver `applyClienteFpRedirectGuard`.
const RAIZ_FINANCIAL_PLANNING = '/financial-planning'

// ★ RF-A12 — cold load de /financial-planning não pode aterrissar o cliente
// no 404.
//
// O `redirect` da raiz é resolvido no `match()`, ANTES deste beforeEach. Num
// cold load `user.loading` ainda é `true`, então `resolveFinancialPlanningRedirect`
// decide só pelas flags e escolhe um destino fechado ao cliente. Aqui o usuário
// JÁ está resolvido (pelo `await P.getCurrentUser()` acima), então corrigimos.
//
// ★ Casa pela ORIGEM (`redirectedFrom === '/financial-planning'`), NUNCA por uma
// lista de destinos. A primeira versão enumerava `['fp.welcome',
// 'fp.simulacoes.pgbl']` e não pegava o ramo do PGBL: `fp.simulacoes.pgbl` tem
// `redirect` PRÓPRIO para `fp.simulacoes.pgbl.simulacoes` (`routes.js:253`), e o
// vue-router encadeia — o nome que chega aqui é o da FOLHA. Enumerar destinos
// obriga a lista a acompanhar a árvore de rotas, e ela já falhou uma vez.
//
// ★ Só corrige quando houve REDIRECT. Cliente que DIGITA
// `/financial-planning/simulacoes/cenario-fiscal` continua recebendo 404: rota
// fechada que passa a redirecionar vira oráculo de existência, e a Parte A
// existe justamente para não ter um. Quem foi MANDADO ao destino não o escolheu.
//
// DEVE ser chamada ANTES do bloco de `customPermission`: ele responde 404 e
// retorna, e depois dele não há mais o que corrigir.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export function applyClienteFpRedirectGuard(routeTo, user, next) {
  if (!routeTo.redirectedFrom) return false
  const origem = String(routeTo.redirectedFrom)
    .split(/[?#]/)[0]
    .replace(/\/$/, '')
  if (origem !== RAIZ_FINANCIAL_PLANNING) return false
  // Já está no destino certo — redirecionar de novo seria navegação redundante.
  if (routeTo.name === 'fp.home') return false
  if (!isCliente(user)) return false
  next({ name: 'fp.home' })
  return true
}

// Nome da rota do chat de IA (`aawz-intelligence/routes.js:95`) — a ABA, dentro
// da moldura do módulo. Destino do guard do próprio módulo, logo abaixo.
const ROTA_CHAT_IA = 'aawz-intelligence.chat-ia'

// ★ A HOME por IA é a visão imersiva (`ROTA_IMERSIVA`, path `/home-ia`), tela
// cheia sem NENHUM chrome da plataforma. Existiu uma versão anterior sem o
// shell do módulo mas ainda com sidebar/topbar (decisão do owner de
// 25/08/2026, sobre o D-5 do design-conformance); consolidada nesta em
// 31/08/2026 — ver comentário na rota. Destino do `applyHomeIaRedirectGuard`.

const RAIZ_AAWZ_INTELLIGENCE = '/aawz-intelligence'

// MUST-3 do PR #1955, mesmo mecanismo do RF-A12 acima:
// `resolveAawzIntelligenceRedirect` resolve no `match()`, antes deste
// beforeEach, então em cold load `parameters/show` ainda está default e
// sempre cai em `welcome`. Casa pela ORIGEM, não pelo destino: clique direto
// na aba "Bem-vindo" não tem `redirectedFrom`, então não é tocado — ao
// contrário do `created()` na view (revertido em d608ec7a2), que bounceava
// esse clique por não distinguir os dois casos. Chamar antes do
// `customPermission`.
export function applyAawzIntelligenceRedirectGuard(routeTo, show, next) {
  if (!routeTo.redirectedFrom) return false
  const origem = String(routeTo.redirectedFrom)
    .split(/[?#]/)[0]
    .replace(/\/$/, '')
  if (origem !== RAIZ_AAWZ_INTELLIGENCE) return false
  // Já está no destino certo — redirecionar de novo seria navegação redundante.
  if (routeTo.name === ROTA_CHAT_IA) return false
  if (!show || !show[FEATURES.CHAT_IA]) return false
  if (!isWelcomeDismissed('aawz-intelligence')) return false
  next({ name: ROTA_CHAT_IA })
  return true
}

const RAIZ_FEE_FIXO = '/fee-fixo'

// ★ G-1 do verify iteração 2 (QA no app real, 21/08/2026) — terceiro caso do
// MESMO defeito do RF-A12 (FP) e do MUST-3 (aawz-intelligence), e o mais caro:
// com as DUAS flags ligadas, URL digitada / F5 / primeira navegação pós-login
// aterrissavam em `fee-fixo.welcome` e nunca chegavam ao guia.
//
// O relógio medido: aos 12-14s a rota resolve com ZERO flag carregada (o
// `redirect` da raiz roda no `match()`, antes deste beforeEach e antes do
// `await P.getCurrentUser()` que popula `parameters/show`); aos 16s as 114
// flags chegam e nada reavalia. Navegar por dentro do app funcionava — a store
// já estava quente —, e era isso que mantinha `fee-fixo/routes.unit.js` verde.
//
// ★ Reexecuta `resolveFeeFixoRedirect` INTEIRA, não só o ramo da IA. O ramo do
// dismiss ("já marquei, me leve à Visão Geral") sofria do mesmo mal sem nunca
// ter sido reportado, e recopiar um ramo só criaria uma segunda régua do mesmo
// redirect, livre para divergir no próximo ajuste.
//
// ★ Casa pela ORIGEM (`redirectedFrom === '/fee-fixo'`) e não por uma lista de
// destinos, pelo motivo já documentado no RF-A12: enumerar destinos obriga a
// lista a acompanhar a árvore de rotas, e ela já falhou uma vez. Quem clica
// direto numa aba não tem `redirectedFrom` e não é tocado — a regressão que
// motivou o revert do `created()` na view do aawz-intelligence.
//
// Chamar ANTES do bloco de `customPermission`: ele responde 404 e retorna.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export function applyFeeFixoRedirectGuard(routeTo, next) {
  if (!routeTo.redirectedFrom) return false
  const origem = String(routeTo.redirectedFrom)
    .split(/[?#]/)[0]
    .replace(/\/$/, '')
  if (origem !== RAIZ_FEE_FIXO) return false
  const destino = resolveFeeFixoRedirect()
  // Já está no destino certo (store quente no match) — redirecionar de novo
  // seria navegação redundante.
  if (!destino || destino.name === routeTo.name) return false
  next(destino)
  return true
}

// Nome da rota da home antiga (`routes.js:110`) — a raiz da plataforma.
const ROTA_HOME = 'home'

// Home por IA na raiz (spec-001 new-ux) — quarto guard da família RF-A12 /
// MUST-3 / G-1: decide no `beforeEach`, DEPOIS do `await P.getCurrentUser()`,
// nunca no `redirect:` da rota (store fria). Casa pelo DESTINO (`name ===
// 'home'`), não pela origem: a raiz não tem `redirect:` ativo (`routes.js`),
// então `redirectedFrom` nunca é carimbado — casar por origem, como os irmãos,
// nunca agia (G-1, QA 25/08/2026). `//` segue 404. Cliente final não
// aterrissa na ferramenta do assessor (I-5), e a decisão é a permissão da
// PRÓPRIA rota de destino (`checkImersivaPermission`, I-4) — falha fechada:
// sem ela, fica na home antiga. Comportamento medido e fixado em `index.unit.js`.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export function applyHomeIaRedirectGuard(routeTo, show, user, next) {
  if (!routeTo || routeTo.name !== ROTA_HOME) return false
  if (isCliente(user)) return false
  if (!checkImersivaPermission({ user: user || {}, show: show || {} })) {
    return false
  }
  next({ name: ROTA_IMERSIVA })
  return true
}

// Nome da rota da Lista de Espera (`professional-waitlist/routes.js`).
const ROTA_LISTA_ESPERA = 'lista-espera'

// ★ Lista de espera (spec-001 onboarding-v2): o profissional que entrou pela
// landing e ainda não foi chamado NÃO tem plataforma — toda URL cai no
// questionário, digitada, favoritada ou vinda de um link.
//
// ★ Usa `estaNaListaDeEspera` (o predicado ÚNICO, `professional-waitlist/utils/
// lista-espera-enabled.js`), que é o MESMO que o resto do módulo consulta.
// Recopiar a régua aqui é exatamente o erro que o cabeçalho de
// `@utils/escritorioFeeFixoMigracao.js:7-10` já registra: duas cópias
// divergem na primeira mudança. Fail-closed vem de lá — mapa de flags vazio
// devolve `false`, nunca `true` por default.
//
// ⚠️ Roda DEPOIS do `await P.getCurrentUser()` porque mora dentro de
// `applyRedirectGuards` (chamado no `beforeEach` já com o usuário resolvido).
// Antes do `await`, `parameters/show` está vazio e o predicado, sendo
// fail-closed, responderia `false` para quem ESTÁ na lista — a tranca abriria
// justo no cold load, que é o caminho mais comum de quem acabou de criar a
// senha. É o mesmo relógio do G-1 do fee-fixo, documentado acima.
//
// ⚠️ Já estar na rota da Lista de Espera não redireciona — senão o `next`
// reentra no `beforeEach` para o mesmo destino e faz laço.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export function applyListaDeEsperaGuard(routeTo, user, show, next) {
  if (!estaNaListaDeEspera({ user, access: show })) return false
  if (routeTo && routeTo.name === ROTA_LISTA_ESPERA) return false
  next({ name: ROTA_LISTA_ESPERA })
  return true
}

// Os quatro guards de redirect num só lugar: cada `if (applyXGuard()) return`
// no `beforeEach` custava +1 de complexidade por guard novo. Nenhuma decisão
// aqui — ordem e guards são os mesmos, `parameters/show` lido uma vez.
// ⚠️ Chamar DEPOIS do `await P.getCurrentUser()` (é dele que vem o `show`
// quente — decidir antes é o defeito canônico da família) e ANTES do bloco de
// `customPermission`, que responde 404 e retorna.
// Retorna true quando algum guard redirecionou (o beforeEach deve parar ali).
export function applyRedirectGuards(routeTo, user, next) {
  const show = store.getters['parameters/show']
  // ★ PRIMEIRO, e de propósito fora da premissa de disjunção dos outros
  // quatro: a Lista de Espera é uma TRANCA, não um redirect de raiz de
  // módulo — o conjunto dela é "todas as rotas menos a própria". Com a flag
  // ligada ela precisa vencer os demais (a raiz com as flags da Home IA, por
  // exemplo, é reivindicada pelos dois); sem a flag, ela não toca em nada e a
  // disjunção dos outros quatro continua valendo como está documentada em
  // `index.unit.js`.
  if (applyListaDeEsperaGuard(routeTo, user, show, next)) return true
  if (applyClienteFpRedirectGuard(routeTo, user, next)) return true
  if (applyAawzIntelligenceRedirectGuard(routeTo, show, next)) return true
  if (applyFeeFixoRedirectGuard(routeTo, next)) return true
  if (applyHomeIaRedirectGuard(routeTo, show, user, next)) return true
  return false
}

// Rotas sempre alcançáveis mesmo por quem cai na regra do escritório Fee Fixo
// Migração (ver @utils/escritorioFeeFixoMigracao): 'home' é o próprio
// redirecionador (view-home.vue manda pro fee-fixo — mesma regra, ver
// redirectRouteByPermissions()), '404' precisa continuar de pé — senão
// bloquear "tudo fora da lista" bloquearia também o próprio destino do
// bloqueio — e 'crm'/'fp'/'aawz-intelligence' foram liberados por pedido do
// usuário (12/08/2026), além do próprio Fee Fixo. Mesma lista que
// MODULE_KEYS_PERMITIDOS em
// `src/modules/navegacao-v2/utils/escritorio-fee-fixo-migracao.js` — as duas
// precisam mudar juntas. `Set` (não Array): uso é só existência via `.has()`
// (SonarQube javascript:S7776, review do PR #1940).
const MODULE_KEYS_LIBERADOS_FEE_FIXO_MIGRACAO = new Set([
  'fee-fixo',
  'home',
  '404',
  'crm',
  'fp',
  'aawz-intelligence',
  // ★ T018 — cinto de segurança da Lista de Espera. Quem entra pela landing
  // nasce no escritório 5914 e NÃO é gestor, ou seja, cai nesta mesma regra:
  // sem esta chave, `applyEscritorioFeeFixoMigracaoGuard` mandaria o
  // profissional para `fee-fixo` e a Lista de Espera ficaria inalcançável se
  // algum dia a ordem dos guards mudar (hoje `applyRedirectGuards` roda
  // antes, e o laço não chega a acontecer — é justamente por depender dessa
  // ordem que a chave precisa estar aqui).
  // Não entra em `MODULE_KEYS_PERMITIDOS` da navbar
  // (`navegacao-v2/utils/escritorio-fee-fixo-migracao.js`) pelo mesmo motivo
  // de 'home' e '404', que também só existem aqui: não há item de menu para a
  // Lista de Espera — a rota é `meta.restrito` e renderiza SEM navbar.
  'lista-espera',
])

// Escritório 5914 (AAWZ Fee Fixo Migração), não-gestor: fecha o buraco que a
// sidebar sozinha não fecha — nenhuma URL fora do módulo Fee Fixo é
// alcançável, mesmo digitada direto (achado do usuário, 12/08/2026). Mesmo
// padrão de applyClienteHiddenRouteGuard/applyClienteFpRedirectGuard acima:
// função própria, testável sem montar o beforeEach inteiro.
// Retorna true quando redirecionou (o beforeEach deve parar ali).
export function applyEscritorioFeeFixoMigracaoGuard(routeTo, user, next) {
  if (!deveVerSoFeeFixo(user)) return false
  const moduleKey = routeModuleKey(routeTo.name)
  if (MODULE_KEYS_LIBERADOS_FEE_FIXO_MIGRACAO.has(moduleKey)) return false
  next({ name: 'fee-fixo' })
  return true
}

// Transição "Iniciar etapa" (2026-07-25, Jornada de Configuração): reseta o
// tint da linha/saída da lista do
// accordion e move o foco pro drawer — só quando a navegação em curso era de
// fato uma "Iniciar etapa" (getter navigatingTaskId setado ANTES do
// router.push por utils/step-navigation.js `startTaskNavigation`, chamada
// direto do clique na linha do accordion — sem modal intermediário).
// Extraída pra função própria (mesmo padrão de
// swallowNavigationFailure/applyClienteHiddenRouteGuard acima) por ser
// testável sem montar o afterEach inteiro.
export function finishStepNavigation() {
  const wasStepNavigation = !!store.getters['centralConfig/navigatingTaskId']
  store.dispatch('centralConfig/resetStepTransition')
  if (wasStepNavigation) GlobalBus.$emit(CENTRAL_CONFIG_FOCUS_GUIDE)
}

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalPush.call(this, location, onResolve, onReject)
  }
  return originalPush.call(this, location).catch(swallowNavigationFailure)
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
  if (onResolve || onReject) {
    return originalReplace.call(this, location, onResolve, onReject)
  }
  return originalReplace.call(this, location).catch(swallowNavigationFailure)
}
/* global newrelic */

// Função para enviar evento para o Google Analytics
function sendAnalyticsEvent({
  previousPage,
  desiredPage,
  reason,
  previousUrl,
  desiredUrl,
}) {
  console.warn('REDIRECIONANDO PARA ')
  try {
    const isDev = [
      'dev.aawz.com.br',
      'whitelabelh.aawz.com.br',
      'whitelabel.aawz.local.com.br',
      'localhost',
    ].includes(window.location.hostname)
    if (!isDev) {
      if (Vue.$gtag) {
        const params = {
          eventCategory: 'Navigation',
          eventAction: 'not_found',
          eventLabel: `From: ${previousPage} (${previousUrl}), To: ${desiredPage} (${desiredUrl}), Reason: ${reason}`,
        }
        Vue.$gtag.event(params.eventAction, {
          event_category: params.eventCategory,
          event_label: params.eventLabel,
          event_action: params.eventAction,
          value: 1,
        })
      }

      if (
        newrelic &&
        newrelic.recordCustomEvent &&
        typeof newrelic.recordCustomEvent === 'function'
      ) {
        newrelic.recordCustomEvent('NavigationError', {
          action: 'Navigation',
          from_url: previousUrl,
          to_name: desiredPage,
          element: reason,
        })
      }
    }
  } catch (err) {
    console.log(err)
    Sentry.captureException(err)
  }
  // if (window.ga) {
  //   window.ga('send', 'event', 'Navigation', 'Redirect to 404', )
  // }
}

const router = new VueRouter({
  routes,
  base: process.env.NODE_ENV === 'production' ? '/investimentos/' : '',
  mode: 'history',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { x: 0, y: 0 }
    }
  },
})

router.onError((error) => {
  if (isChunkLoadError(error)) {
    handleChunkError()
  }
})

router.beforeEach(async (routeTo, routeFrom, next) => {
  // Captura `?login_method=...` anexado pelo app de login ao destino do
  // redirect: persiste em sessionStorage e limpa o param da URL. O evento em si
  // é disparado em getCurrentUser (parameters.js) no primeiro load autenticado.
  captureLoginMethodFromQuery(routeTo, router)

  // If this isn't an initial page load...
  if (routeFrom.name !== null) {
    // Start the route progress bar.
    NProgress.start()
  }

  if (
    routeTo.fullPath === routeFrom.fullPath &&
    routeTo.name === routeFrom.name
  ) {
    console.warn('Tentando redirecionar para a mesma página, bloqueado')
    return
  }

  let user = store.getters['user/currentUser'] || {}
  // const featureAccess = store.getters['parameters/show'] || {}

  if (user.loading) {
    // TODO: jogar redirect por permissão para main.vue
    await P.getCurrentUser()
  }

  user = store.getters['user/currentUser'] || {}

  // setar a ultima rota antes de mudar
  store.dispatch('global/setPreviousPage', routeFrom)

  // TODO: Remover comentario para bloquear parametrizacao
  if (user.parametrizar && routeTo.meta && routeTo.meta.parametrizar) {
    next({ name: 'parametrizacao' })
    return
  }

  // TODO: Bloqueando para não-whitelabels
  if (routeTo.meta && routeTo.meta.whitelabel_only) {
    if (!window.WHITELABEL) {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Not whitelabel',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' })
      return
    }
  }

  if (routeTo.meta && routeTo.meta.can_show) {
    const show = store.getters['parameters/show']
    const canShow = routeTo.meta.can_show

    if (Array.isArray(canShow)) {
      const valid = canShow.every((can) => {
        return show[can]
      })

      if (!valid) {
        sendAnalyticsEvent({
          previousPage: routeFrom.name,
          desiredPage: routeTo.name,
          reason: 'Permission validation failed',
          previousUrl: routeFrom.fullPath,
          desiredUrl: routeTo.fullPath,
        })
        next({ name: '404' })
        return
      }
    } else if (!show[routeTo.meta.can_show]) {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Permission validation failed',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' })
      return
    }
  }

  if (routeTo.meta && routeTo.meta.listCanShow) {
    // Lista com as permissoes
    const show = Object.entries(store.getters['parameters/show'] || {})
      .filter((item) => item[1])
      .map((item) => item[0])
    if (!routeTo.meta.listCanShow.some((item) => show.includes(item))) {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Permission validation failed',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' })
      return
    }
  }

  if (routeTo.meta && routeTo.meta.only_aawz) {
    // Lógica para acesso foi alterado para ser possível acessar em quaisquer escritório
    // desde que seja feito por alguém interno
    const showFlags = store.getters['parameters/show'] || {}
    const hasExternalFlagAccess =
      routeTo.meta.external_flag && showFlags[routeTo.meta.external_flag]
    if ((!user.IS_INTERNAL || !user.id_interno) && !hasExternalFlagAccess) {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Permission internal validation failed',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' })
      return
    }
    // if (process.env.NODE_ENV !== 'development') {
    //   if (!['115', '233'].includes(user.id_interno.toString())) {
    //     next({ name: '404' })
    //     return
    //   }
    // }
  }

  if (await applyClienteGuards(routeTo, next)) return

  if (routeTo.meta && routeTo.meta.go_out) {
    window.location.href =
      (process.env.NODE_ENV !== 'production' ? 'https://dev.aawz.com.br' : '') +
      routeTo.meta.go_out
    return
  }

  if (applyRedirectGuards(routeTo, user, next)) return

  // Um predicado só para o `customPermission` — o mesmo que `applyClienteGuards`
  // consulta antes de entrar no cliente da query (review do PR #2261, N-01).
  if (!rotaPassaNoCustomPermission(routeTo)) {
    sendAnalyticsEvent({
      previousPage: routeFrom.name,
      desiredPage: routeTo.name,
      reason: 'Custom permission validation failed',
      previousUrl: routeFrom.fullPath,
      desiredUrl: routeTo.fullPath,
    })
    next({ name: '404' })
    return
  }

  if (applyEscritorioFeeFixoMigracaoGuard(routeTo, user, next)) return

  const permission = (routeTo.meta || {}).permission || []

  if (routeTo.meta && routeTo.meta.internal) {
    if (!user.IS_INTERNAL) {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Permission internal validation failed',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' })
      return
    }
  }

  if (permission.length) {
    const isManager = (user.tipo_usuario || '').toLowerCase() === 'gestor'
    const isToAll = permission.includes('all')
    const isForType = permission.includes(user.tipo_usuario)
    const isForRole = permission.includes(user.role)
    const hasPermission =
      routeTo.meta && routeTo.meta.can_show
        ? store.getters['parameters/canShow'](routeTo.meta.can_show)
        : true
    const isForUser = isForType || isForRole || hasPermission
    if (
      isManager ||
      isToAll ||
      isForUser ||
      (isManager && !permission.includes('cliente'))
    ) {
      next()
    } else {
      sendAnalyticsEvent({
        previousPage: routeFrom.name,
        desiredPage: routeTo.name,
        reason: 'Last Permission validation failed',
        previousUrl: routeFrom.fullPath,
        desiredUrl: routeTo.fullPath,
      })
      next({ name: '404' }) // TODO: pagina para falta de permissao
    }
  } else {
    next()
  }
})

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              if (routeFrom.name === args[0].name) {
                // Complete the animation of the route progress bar.
                NProgress.done()
              }
              // Complete the redirect.
              next(...args)
              reject(new Error('Redirected'))
            } else {
              resolve()
            }
          })
        } else {
          // Otherwise, continue resolving the route.
          resolve()
        }
      })
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return
  }

  // If we reach this point, continue resolving the route.
  next()
})

// When each route is finished evaluating...
router.afterEach((routeTo, routeFrom) => {
  // Limpeza das flags de chunk error acontece no boot da SPA (main.js),
  // não aqui — uma navegação bem-sucedida pós-modal não é sinal confiável
  // de que o ambiente está saudável (o usuário pode ainda estar no HTML
  // antigo e só ter trocado de rota cliente-side sem carregar chunk novo).
  // Complete the animation of the route progress bar.
  // TODO: se necessário abrir na última página
  // store.dispatch('parameters/saveConfigLocal', {last_page: routeTo.name})
  NProgress.done()
  finishStepNavigation()

  try {
    const isDev = [
      'dev.aawz.com.br',
      'whitelabelh.aawz.com.br',
      'whitelabel.aawz.local.com.br',
      'localhost',
    ].includes(window.location.hostname)
    if (!isDev) {
      if (Vue.$gtag) {
        // Um page_view por navegação. Não emitir também screen_view aqui: o
        // GA4 soma page_view + screen_view em screenPageViews, e a chamada
        // dupla inflava páginas/sessão em ~1,6x (medido em ago/2026 —
        // 112.387 page_view contra 177.244 screenPageViews no mês).
        Vue.$gtag.pageview({
          page_path: routeTo.fullPath,
          page_title: routeTo.name,
          app_name: window.location.hostname,
        })
      }
    }
  } catch (err) {
    console.log(err)
    // Sentry.setContext('user', { id: user.id, email: user.email })
    Sentry.captureException(err)
  }
})

export default router
